import { useCallback, useEffect, useRef, useState } from "react";
import { isEmpty, uniq, flatten, uniqWith, isEqual, zip } from "lodash";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { LSubTitle } from "../../../common/components/LSubtitle";
import { concatenateBlobs, getAudioConcatenationType } from "../../../common/helpers/blobHelper";
import { sleepFor, useCountDown } from "../../../common/helpers/countHelper";
import { useApi } from "../../../services/useApi";
import { AssessmentShell } from "../components/AssessmentShell";
import { useCustomSpeechToText } from "../hooks/useCustomSpeechToText";
import { playAudioWithRetryAsync, useAudio } from "../hooks/useAudio";
import { useScreenWakeLock } from "../hooks/useScreenWakeLock";
import { useNavigate } from "react-router-dom";
import { NavItems } from "../../../common/helpers/navigation";
import {
    CategorizedWords,
    filterAndMatch,
    flattenAndCategorize,
    getUniqueWords,
    getWordsAndAlternatesFromList
} from "../../../common/helpers/instructionHelper";
import { ResultType } from "../../../utils/stt/useSpeechToText";
import { Flow, updateCurrentFlow } from "../../../common/store/assessmentFlowSlice";
import { Word } from "../../../common/store/assessmentSlice";
import { getLocale, translate, Translate } from "react-i18nify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Colors } from "@styles/colors";
import { Env } from "../../../env";
import { LProgressCircle } from "@components/LProgressCircle";
import { LFlex } from "@components";
import { Headset } from "@assets";
import { AssessmentTimeout } from "@fassessment/components/AssessmentTimeout";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { SttSourceType } from "@store/generalTypes";
import { trackDiagnosticException, trackDiagnosticTrace } from "@utils/diagnostics/appInsightsDiagnostics";

const LIST_A = 0;
const LIST_B = 1;

interface TextAudio {
    text: string;
    audio: string;
}

const getTextAndAudio = (currentFlow: Flow): TextAudio => {
    const defaultValue = {
        text: translate("assessments.allWordsJustRead"),
        audio: `${Env.REACT_APP_BLOB_URL}/instructions-${getLocale()}-us/allWordsJustRead.mp3`
    };

    switch (currentFlow.name) {
        case "List A Free Recall 1":
            return defaultValue;
        case "List B Free Recall 1":
            return {
                text: translate("assessments.allWordsJustReadNewList"),
                audio: `${Env.REACT_APP_BLOB_URL}/instructions-${getLocale()}-us/allWordsJustReadNewList.mp3`
            };
        case "List A Free Recall 2":
            return {
                text: translate("assessments.allWordsVeryFirst").replace("{{blobUrl}}", Env.REACT_APP_BLOB_URL),
                audio: ""
            };
        default:
            return defaultValue;
    }
};

export const AssessmentFreeRecall = (): JSX.Element => {
    const responses = useRef<ResultType[]>([]);
    const uploadResultInFlightRef = useRef(false);
    const responseTime = useAppSelector((x) => x.globalSettings.freeRecall);
    const timeout = useAppSelector((x) => x.globalSettings.freeRecallTimeout);
    const showTimer = useAppSelector((x) => x.globalSettings.showTimers);
    const sttDiagnosticsEnabled = useAppSelector((x) => x.globalSettings.enableSttDiagnostics);
    const wakeLockEnabled = useAppSelector((x) => x.globalSettings.enableAssessmentWakeLock);

    const [uploading, setUploading] = useState(false);
    const [promptPlayed, setPromptPlayed] = useState(false);
    const [categorizedWords, setCategorizedWords] = useState<CategorizedWords>({});

    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const appInsights = useAppInsightsContext();

    const assessment = useAppSelector((x) => x.assessment);

    const [assessmentFlow] = useState(useAppSelector((x) => x.assessmentFlow));

    const currentFlow = assessmentFlow.flows[assessmentFlow.currentFlow];

    useScreenWakeLock({
        enabled: wakeLockEnabled,
        diagnosticsEnabled: sttDiagnosticsEnabled,
        area: "free-recall",
        context: {
            flowName: currentFlow.name
        }
    });

    const currentListIndex = currentFlow.list === -1 ? 0 : currentFlow.list;
    const currentListWordsWithAlternate = getWordsAndAlternatesFromList(assessment.wordLists[currentListIndex].words);

    const textAudio = getTextAndAudio(currentFlow);

    const listA = assessment.wordLists[LIST_A].words;
    const listB = assessment.wordLists[LIST_B].words;

    const allTargetWords = [...listA.map((x) => x.value), ...listB.map((x) => x.value)];
    const currentListWords = uniq([...currentListWordsWithAlternate, ...allTargetWords]);

    const { bulkAssessmentResponse, uploadAssessmentStepResponse } = useApi();

    const handlerOnStartedSpeakingOverride = (): void => {
        cancelTimeoutCounter();
    };

    useEffect(() => {
        if (assessmentFlow.flows[assessmentFlow.currentFlow + 1]) {
            dispatch(updateCurrentFlow());
        }

        let newCategorizedWords: CategorizedWords = {};
        newCategorizedWords = flattenAndCategorize(
            newCategorizedWords,
            listA,
            currentFlow.list !== LIST_A && currentFlow.list !== -1
        );
        newCategorizedWords = flattenAndCategorize(
            newCategorizedWords,
            listB,
            currentFlow.list !== LIST_B && currentFlow.list !== -1
        );
        newCategorizedWords = flattenAndCategorize(newCategorizedWords, assessment.intrusions as Word[], true);

        setCategorizedWords(newCategorizedWords);
    }, []);

    const { results, startSpeechToText, stopSpeechToText, recordingVolume, stream, destroy, isRecording } =
        useCustomSpeechToText({
            context: currentListWords,
            fullAudio: true,
            useStopTalking: false,
            handlerOnStartedSpeakingOverride
        });

    const handlePrompt = useCallback(async () => {
        trackDiagnosticTrace({
            enabled: sttDiagnosticsEnabled,
            area: "free-recall",
            event: "prompt:begin",
            properties: {
                flowName: currentFlow.name,
                contextWordCount: currentListWords.length,
                hasPromptAudio: !!textAudio.audio
            }
        });

        if (textAudio.audio) {
            const audioPlayed = await playAudioWithRetryAsync(() => new Audio(textAudio.audio));

            if (!audioPlayed) {
                trackDiagnosticTrace({
                    enabled: sttDiagnosticsEnabled,
                    area: "free-recall",
                    event: "prompt:audio-failed",
                    properties: {
                        flowName: currentFlow.name,
                        audioUrl: textAudio.audio
                    }
                });

                return;
            }
        }

        await startSpeechToText(undefined, currentListWords, { stepName: currentFlow.name });
        resetTimeoutCounter();
        resetCounter();
        setPromptPlayed(true);

        trackDiagnosticTrace({
            enabled: sttDiagnosticsEnabled,
            area: "free-recall",
            event: "prompt:recording-started",
            properties: {
                flowName: currentFlow.name
            }
        });
    }, [stream]);

    const handleOnCounterDone = async (): Promise<void> => {
        trackDiagnosticTrace({
            enabled: sttDiagnosticsEnabled,
            area: "free-recall",
            event: "timer:end",
            properties: {
                flowName: currentFlow.name,
                responseCount: responses.current.length
            }
        });

        const stopSucceeded = await stopSpeechToText(true, undefined, currentListWords, {
            shouldEnd: true
        });

        setUploading(true);

        if (!stopSucceeded) {
            trackDiagnosticTrace({
                enabled: sttDiagnosticsEnabled,
                area: "free-recall",
                event: "timer:stop-without-recorder",
                properties: {
                    flowName: currentFlow.name,
                    responseCount: responses.current.length
                }
            });
            void uploadResult();
        }
    };

    const [counter, resetCounter] = useCountDown({
        from: responseTime,
        onDone: handleOnCounterDone,
        startOnMount: false
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, resetTimeoutCounter, cancelTimeoutCounter] = useCountDown({
        from: timeout,
        onDone: () => {
            void handleNoResponses();
        },
        startOnMount: false
    });

    const [playAudio] = useAudio();

    const uploadResult = async (): Promise<void> => {
        if (uploadResultInFlightRef.current) {
            return;
        }

        uploadResultInFlightRef.current = true;
        const blobs = responses.current.map((x) => x.speechBlob).filter((blob): blob is Blob => !!blob);
        const oneBlob = await concatenateBlobs(blobs, getAudioConcatenationType(blobs));

        if (!isEmpty(responses.current) && oneBlob) {
            const responsesWords = getUniqueWords(responses.current);
            const uniqWords = uniq(flatten(responsesWords));

            let filteredWords = filterAndMatch(uniqWords, categorizedWords);
            filteredWords = uniqWith(
                filteredWords.filter((x) => !x.response.includes(" ")),
                isEqual
            );

            const stepName = currentFlow.name;

            const assessmentId = assessment.id ?? 0;

            try {
                await bulkAssessmentResponse(assessmentId, {
                    stepName,
                    responses: filteredWords
                });
            } catch (er) {
                trackDiagnosticException({
                    enabled: sttDiagnosticsEnabled,
                    area: "free-recall",
                    event: "upload:bulk-response:error",
                    error: er,
                    properties: {
                        assessmentId,
                        stepName
                    }
                });
                uploadResultInFlightRef.current = false;
                setUploading(false);
                return;
            }

            const success = await uploadAssessmentAudioStepResponse(assessmentId, stepName, oneBlob);

            if (!success) {
                await uploadAssessmentAudioStepResponse(assessmentId, stepName, oneBlob);
            }
        }

        await sleepFor(2);
        destroy();

        if (!assessmentFlow.flows[assessmentFlow.currentFlow + 1]) {
            navigate(NavItems.Assessments.AssessmentClosingVideo.path);
            return;
        }

        navigate(assessmentFlow.flows[assessmentFlow.currentFlow + 1].path);
    };

    const uploadAssessmentAudioStepResponse = async (
        assessmentId: number,
        stepName: string,
        audio: Blob
    ): Promise<boolean> => {
        let result = true;

        try {
            await uploadAssessmentStepResponse(assessmentId, stepName, audio);
        } catch (error) {
            if (sttDiagnosticsEnabled) {
                appInsights.trackTrace({
                    message: JSON.stringify(error),
                    severityLevel: SeverityLevel.Information
                });
            }
            trackDiagnosticException({
                enabled: sttDiagnosticsEnabled,
                area: "free-recall",
                event: "upload:audio:error",
                error,
                properties: {
                    assessmentId,
                    stepName,
                    blobSize: audio.size,
                    blobType: audio.type
                }
            });

            result = false;
        }

        return result;
    };

    const handleNoResponses = async (): Promise<void> => {
        playAudio(translate("assessments.doYourBestAudio").replace("{{blobUrl}}", Env.REACT_APP_BLOB_URL), () => {});
        resetCounter();
        cancelTimeoutCounter();
    };

    useEffect(() => {
        if (!results?.length) return;

        trackDiagnosticTrace({
            enabled: sttDiagnosticsEnabled,
            area: "free-recall",
            event: "results:received",
            properties: {
                flowName: currentFlow.name,
                resultCount: results.length,
                shouldEnd: results[0]?.context?.shouldEnd,
                sources: results.map((x) => x.source).join("|"),
                blobSize: results[0]?.speechBlob?.size
            }
        });

        const googleUniqList = flatten(
            results
                .filter((x) => x.source === SttSourceType.Google)
                .map((x) => uniq(x.transcript.toLocaleLowerCase().split(" ")))
        );
        const azureUniqList = flatten(
            results
                .filter((x) => x.source === SttSourceType.Azure)
                .map((x) => uniq(x.transcript.toLocaleLowerCase().split(" ")))
        );

        const interleaved = zip(googleUniqList, azureUniqList);
        const mergedList = flatten(interleaved).filter((item) => item !== undefined);
        const joinResults = uniq(mergedList).join(" ").trim();

        const orderedResults = results.sort((x, y) => y.confidence - x.confidence);

        const highestConfidenceResult = orderedResults[0];

        const res: ResultType = {
            transcript: joinResults,
            speechBlob: highestConfidenceResult.speechBlob,
            elapsed: highestConfidenceResult.elapsed,
            intendedWord: highestConfidenceResult.intendedWord,
            confidence: highestConfidenceResult.confidence,
            context: highestConfidenceResult.context
        };

        if (res && res.transcript !== "-") {
            responses.current = [...responses.current, res];
        }

        if (res.context.shouldEnd) {
            if (isEmpty(responses.current)) {
                responses.current = [...responses.current, res];
            }

            void uploadResult();
        }
    }, [results]);

    useEffect(() => {
        if (stream && !promptPlayed) {
            void handlePrompt();
        }
    }, [stream]);

    const progress = Math.floor(((responseTime - counter) * 100) / responseTime);
    const time = new Date((responseTime - (responseTime - counter)) * 1000).toISOString().slice(14, 19);

    return (
        <AssessmentShell micIsRecording={isRecording} micVolume={recordingVolume}>
            <div className="assessment-stage" style={{ paddingBottom: "6rem" }}>
                {!uploading ? (
                    <>
                        {showTimer && (
                            <div className="assessment-timer" style={{ position: "absolute", right: "8%", top: "2%" }}>
                                <LProgressCircle
                                    progress={progress}
                                    text={time}
                                    labelColor={Colors.primaryBlue}
                                    trackColor={Colors.primaryBlue}
                                    indicatorColor={Colors.primaryBlue}
                                />
                                <div>{results[0]?.transcript}</div>
                            </div>
                        )}
                        <LFlex justifyCenter alignCenter>
                            <LSubTitle size="1" color="has-text-dark-blue">
                                {textAudio.text}
                                <p className="is-size-1 has-text-dark-blue">
                                    <Translate value="assessments.doYourBest" />
                                </p>
                            </LSubTitle>
                            <LFlex className={isRecording ? "" : "is-hidden-custom"} row alignCenter justifyCenter>
                                <Headset />
                                <p className="mx-4 has-text-dark-blue">
                                    <Translate value="assessments.listening" />
                                    ...
                                </p>
                            </LFlex>
                        </LFlex>
                    </>
                ) : (
                    <div>
                        <LSubTitle size="1" color="has-text-dark-blue">
                            <Translate value="assessments.pleaseWait" />
                        </LSubTitle>
                        <FontAwesomeIcon icon={faSpinner} size="6x" color={Colors.darkBlue} className="fa-spin" />
                    </div>
                )}
            </div>
            <AssessmentTimeout micVolume={recordingVolume} />
        </AssessmentShell>
    );
};
